import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Card from '@/components/ui/Card';
import { useAuth } from '@/lib/auth-context';
import { getSponsorWallet, getSponsorPacks, sponsorCheckout } from '@/lib/coreApi';
import {
  creditValueCentsToCredits,
  creditValueCentsToUsd,
  creditsToUsd,
  formatCreditsLabel,
  formatCreditsValue,
  formatUsd,
  ledgerCentsToCredits,
} from '@/utils/credits';
import GiftingForm from '@/components/sponsor/GiftingForm';

interface WalletAccount {
  id: string;
  balance_credits?: number;
  balance_cents?: number;
  share_link?: string | null;
}

interface LedgerEntry {
  id: string;
  type: string;
  delta_credits?: number;
  delta_cents?: number;
  source?: string | null;
  reference_id?: string | null;
  created_at?: string | null;
  note?: string | null;
}

interface CreditPack {
  id: string;
  name: string;
  key?: string;
  slug?: string;
  usd_price: number;
  credit_value_cents: number;
}

const ledgerLabels: Record<string, string> = {
  pack_purchase: 'Pack purchase',
  gift_in: 'Gift received',
  gift_out: 'Gift sent',
  spend: 'Marketplace spend',
  refund: 'Refund',
  platform_grant: 'Platform grant',
  admin_adjust: 'Admin adjustment',
};

export default function SponsorWallet() {
  const { session, profile } = useAuth();
  const token = session?.access_token || null;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [account, setAccount] = useState<WalletAccount | null>(null);
  const [ledger, setLedger] = useState<LedgerEntry[]>([]);
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [packsError, setPacksError] = useState('');
  const [packsLoading, setPacksLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const { showToast, ToastHost } = useToast();
  const giftShareCode = (searchParams.get('share') || '').trim();
  const [creditIssueOpen, setCreditIssueOpen] = useState(false);
  const [creditIssueSubject, setCreditIssueSubject] = useState('Issue with wallet credits');
  const [creditIssueMessage, setCreditIssueMessage] = useState('');
  const [creditIssueSending, setCreditIssueSending] = useState(false);
  const [creditIssueError, setCreditIssueError] = useState('');

  useEffect(() => {
    if (!token) {
      setAccount(null);
      setLedger([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError('');

    const load = async () => {
      try {
        const result = await getSponsorWallet();
        if (cancelled) return;
        const acct = result?.data?.account;
        setAccount(acct?.id ? { ...acct, id: acct.id } as WalletAccount : null);
        setLedger(Array.isArray(result?.data?.ledger) ? result.data.ledger as LedgerEntry[] : []);
      } catch (err: unknown) {
        if (cancelled) return;
        setAccount(null);
        setLedger([]);
        setError(err instanceof Error ? err.message : 'Unable to load wallet');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!token) {
      setPacks([]);
      setPacksLoading(false);
      return;
    }

    let cancelled = false;
    setPacksLoading(true);
    setPacksError('');

    const load = async () => {
      try {
        const result = await getSponsorPacks();
        if (cancelled) return;
        setPacks(Array.isArray(result?.data?.items) ? result.data.items : []);
      } catch (err: unknown) {
        if (cancelled) return;
        setPacks([]);
        setPacksError(err instanceof Error ? err.message : 'Unable to load credit packs');
      } finally {
        if (!cancelled) setPacksLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const balanceCredits = useMemo(() => {
    if (!account) return 0;
    if (typeof account.balance_credits === 'number') {
      return Number(account.balance_credits);
    }
    return creditValueCentsToCredits(account?.balance_cents ?? 0);
  }, [account?.balance_credits, account?.balance_cents]);
  const balanceUsd = useMemo(() => creditsToUsd(balanceCredits), [balanceCredits]);

  const startCheckout = async (pack: CreditPack) => {
    if (!token) return;
    const successUrl =
      typeof window !== 'undefined'
        ? `${window.location.origin}/sponsor/wallet?status=success`
        : undefined;
    const cancelUrl =
      typeof window !== 'undefined'
        ? `${window.location.origin}/sponsor/wallet?status=cancelled`
        : undefined;

    try {
      const result = await sponsorCheckout({
        pack_id: pack?.id,
        pack_key: pack?.key || pack?.slug || undefined,
        success_url: successUrl,
        cancel_url: cancelUrl,
      });
      if (result?.data?.url && typeof window !== 'undefined') {
        window.location.assign(result.data.url);
      }
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Unable to start checkout');
    }
  };

  const submitCreditIssue = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!token || !account?.id) {
      setCreditIssueError('Wallet data unavailable.');
      return;
    }
    if (!creditIssueMessage.trim()) {
      setCreditIssueError('Please describe the issue.');
      return;
    }
    setCreditIssueSending(true);
    setCreditIssueError('');
    try {
      const preview = ledger.slice(0, 5).map((entry) => ({
        id: entry.id,
        type: entry.type,
        delta_credits:
          typeof entry.delta_credits === 'number'
            ? Number(entry.delta_credits)
            : ledgerCentsToCredits(entry.delta_cents ?? 0),
        source: entry.source || null,
        reference_id: entry.reference_id || null,
        created_at: entry.created_at || null,
        note: entry.note || null,
      }));
      const payload = {
        subject: creditIssueSubject || 'Issue with wallet credits',
        message: creditIssueMessage.trim(),
        priority: 'high',
        category: 'credits',
        contextMeta: {
          surface: 'sponsor_portal',
          sponsor_id: profile?.id,
          wallet_context: 'sponsor',
          credit_account_id: account.id,
        },
        credit_context: {
          account_id: account.id,
          balance_credits: Math.round(balanceCredits),
          ledger_entry_ids: preview.map((entry) => entry.id).filter(Boolean),
          ledger_preview: preview,
          share_link: account.share_link || null,
        },
      };
      const response = await fetch('/api/v1/tickets', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(json?.error?.message || json?.error || 'Unable to submit ticket');
      }
      setCreditIssueOpen(false);
      setCreditIssueSubject('Issue with wallet credits');
      setCreditIssueMessage('');
      showToast('Credit issue reported');
    } catch (err: unknown) {
      setCreditIssueError(err instanceof Error ? err.message : 'Unable to submit ticket');
    } finally {
      setCreditIssueSending(false);
    }
  };

  const formattedLedger = useMemo(() => {
    return ledger.map((entry) => {
      const deltaCredits =
        typeof entry.delta_credits === 'number'
          ? Number(entry.delta_credits)
          : ledgerCentsToCredits(entry.delta_cents ?? 0);
      const deltaUsd = creditsToUsd(deltaCredits);
      const isPositive = deltaCredits >= 0;
      const createdAt = entry.created_at ? new Date(entry.created_at) : null;

      return {
        id: entry.id,
        type: ledgerLabels[entry.type] || 'Movement',
        note: entry.note || null,
        source: entry.source || null,
        timestamp: createdAt ? createdAt.toLocaleString() : '—',
        displayAmount: `${isPositive ? '+' : '−'}${formatCreditsValue(Math.abs(deltaCredits))} credits`,
        displayUsd: `${isPositive ? '+' : '−'}${formatUsd(Math.abs(deltaUsd))}`,
      };
    });
  }, [ledger]);

  const isSponsor = profile?.role === 'sponsor';
  const canReportCreditIssue = Boolean(account?.id && token);

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-8">
      <header className="flex flex-col gap-3">
        <Link to="/sponsor/dashboard" className="text-sm text-cocoa/70 underline">
          ← Back to Sponsor Home
        </Link>
        <h1 className="text-3xl font-semibold text-cocoa">Tower Credits Wallet</h1>
        <p className="text-sm text-cocoa/70">
          Tower Credits are platform credits, not money or crypto. Non-redeemable off-platform.
        </p>
      </header>

      {!isSponsor ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          Sponsor access required. Please sign in with your sponsor account.
        </div>
      ) : null}

      <section className="grid gap-6 lg:grid-cols-[1.2fr,0.8fr]">
        <Card className="space-y-5 bg-white/90 p-6">
          <div>
            <p className="text-xs uppercase tracking-wide text-cocoa/60">Current balance</p>
            <div className="text-4xl font-semibold text-cocoa">
              {loading ? (
                '—'
              ) : (
                <>
                  {formatCreditsLabel(balanceCredits)}
                  <span className="block text-base font-normal text-cocoa/60">≈ {formatUsd(balanceUsd)}</span>
                </>
              )}
            </div>
          </div>
          {error ? <p className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-600">{error}</p> : null}
          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-cocoa">Recent activity</h2>
            <ul className="space-y-2 text-sm text-cocoa/75">
              {loading ? (
                <li className="text-cocoa/60">Loading activity…</li>
              ) : formattedLedger.length ? (
                formattedLedger.map((item) => (
                  <li key={item.id} className="rounded-2xl border border-sand/60 bg-white/80 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-medium text-cocoa">{item.type}</p>
                        {item.note ? <p className="text-xs text-cocoa/60">{item.note}</p> : null}
                        {item.source ? <p className="text-xs text-cocoa/60">Source: {item.source}</p> : null}
                      </div>
                      <div className="text-right">
                        <span
                          className={`block text-base font-semibold ${
                            item.displayAmount.startsWith('+') ? 'text-emerald-600' : 'text-brick'
                          }`}
                        >
                          {item.displayAmount}
                        </span>
                        <span className="text-xs text-cocoa/60">{item.displayUsd} equivalent</span>
                      </div>
                    </div>
                    <p className="mt-1 text-xs uppercase tracking-wide text-cocoa/50">{item.timestamp}</p>
                  </li>
                ))
              ) : (
                <li className="rounded-2xl border border-dashed border-sand/60 bg-shell/40 p-3 text-cocoa/60">
                  No movements yet. Purchase a pack or gift credits to get started.
                </li>
              )}
            </ul>
          </div>
        </Card>

        <Card className="space-y-4 bg-white/90 p-6">
          <h2 className="text-lg font-semibold text-cocoa">Gift credits using a share code</h2>
          <p className="text-sm text-cocoa/70">
            Paste a teacher&apos;s Tower share code to send credits. All gifts flow through secure share links.
          </p>
          <GiftingForm
            defaultShareCode={giftShareCode}
            onSuccess={async () => {
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new Event('tower-wallet-refresh'));
              }
              await refreshWallet(token, setAccount, setLedger, setError, setLoading);
            }}
          />
        </Card>
      </section>

      <Card className="space-y-3 bg-white/90 p-6">
        <h2 className="text-lg font-semibold text-cocoa">Need help with Tower Credits?</h2>
        <p className="text-sm text-cocoa/70">
          If your wallet balance or ledger looks off, send the support team a ticket with your wallet snapshot.
        </p>
        <button
          type="button"
          onClick={() => {
            setCreditIssueError('');
            setCreditIssueOpen(true);
          }}
          disabled={!canReportCreditIssue}
          className="rounded-full bg-cocoa px-5 py-2 text-sm font-semibold text-white disabled:opacity-60"
        >
          Report a credit issue
        </button>
        {!canReportCreditIssue ? (
          <p className="text-xs text-cocoa/60">Wallet data required to create a support ticket.</p>
        ) : null}
      </Card>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-cocoa">Buy more Tower Credits</h2>
        {packsError ? <p className="text-sm text-brick">{packsError}</p> : null}
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {packsLoading ? (
            <Card className="bg-white/80 p-6 text-sm text-cocoa/60">Loading packs…</Card>
          ) : packs.length ? (
            packs.map((pack) => (
              <Card key={pack.id} className="space-y-3 bg-gradient-to-br from-white via-shell to-white p-6">
                <div>
                  <p className="text-sm uppercase tracking-wide text-cocoa/60">{pack.name}</p>
                  <p className="text-2xl font-semibold text-cocoa">
                    ${(Number(pack.usd_price) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-cocoa/60">
                    {formatCreditsLabel(creditValueCentsToCredits(pack.credit_value_cents))} value (≈{' '}
                    {formatUsd(creditValueCentsToUsd(pack.credit_value_cents))})
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => startCheckout(pack)}
                  className="w-full rounded-xl bg-cocoa px-4 py-2 text-sm font-semibold text-white transition hover:bg-cocoa/90"
                >
                  Buy pack
                </button>
              </Card>
            ))
          ) : (
            <Card className="bg-white/80 p-6 text-sm text-cocoa/60">No packs available right now.</Card>
          )}
        </div>
      </section>

      {creditIssueOpen ? (
        <div
          role="presentation"
          className="fixed inset-0 z-40 flex items-center justify-center bg-cocoa/40 px-4 py-8"
          onClick={() => {
            if (!creditIssueSending) {
              setCreditIssueError('');
              setCreditIssueOpen(false);
            }
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-cocoa">Report a credit issue</h2>
              <button
                type="button"
                onClick={() => {
                  setCreditIssueError('');
                  setCreditIssueOpen(false);
                }}
                disabled={creditIssueSending}
                className="rounded-full border border-sand px-3 py-1 text-sm font-semibold text-cocoa hover:bg-shell disabled:opacity-60"
              >
                Close
              </button>
            </div>
            <form onSubmit={submitCreditIssue} className="space-y-3">
              <input
                type="text"
                value={creditIssueSubject}
                onChange={(event) => setCreditIssueSubject(event.target.value)}
                className="w-full rounded-xl border border-sand px-3 py-2 text-sm"
                placeholder="Subject"
              />
              <textarea
                value={creditIssueMessage}
                onChange={(event) => setCreditIssueMessage(event.target.value)}
                className="min-h-[140px] w-full rounded-xl border border-sand px-3 py-2 text-sm"
                placeholder="Describe what looks wrong"
                required
              />
              <p className="text-xs text-cocoa/60">
                We&apos;ll include your wallet account, current balance, and the last few ledger entries in the ticket metadata for
                faster triage.
              </p>
              {creditIssueError ? (
                <p className="text-sm text-brick">{creditIssueError}</p>
              ) : null}
              <div className="flex flex-wrap justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setCreditIssueError('');
                    setCreditIssueOpen(false);
                  }}
                  disabled={creditIssueSending}
                  className="rounded-full border border-sand px-4 py-2 text-sm font-semibold text-cocoa hover:bg-shell disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creditIssueSending}
                  className="rounded-full bg-cocoa px-5 py-2 text-sm font-semibold text-white disabled:opacity-60"
                >
                  {creditIssueSending ? 'Submitting…' : 'Submit ticket'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      <ToastHost />
    </div>
  );
}

async function refreshWallet(
  _token: string | null,
  setAccount: React.Dispatch<React.SetStateAction<WalletAccount | null>>,
  setLedger: React.Dispatch<React.SetStateAction<LedgerEntry[]>>,
  setError: React.Dispatch<React.SetStateAction<string>>,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
) {
  if (!_token) return;
  setLoading(true);
  setError('');
  try {
    const result = await getSponsorWallet();
    const acct = result?.data?.account;
    setAccount(acct?.id ? { ...acct, id: acct.id } as WalletAccount : null);
    setLedger(Array.isArray(result?.data?.ledger) ? result.data.ledger as LedgerEntry[] : []);
  } catch (err: unknown) {
    setAccount(null);
    setLedger([]);
    setError(err instanceof Error ? err.message : 'Unable to refresh wallet');
  } finally {
    setLoading(false);
  }
}

function useToast() {
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!message) return undefined;
    const id = setTimeout(() => setMessage(''), 2200);
    return () => clearTimeout(id);
  }, [message]);

  const ToastHost = () =>
    message ? (
      <div className="fixed bottom-5 left-1/2 z-[2000] -translate-x-1/2 rounded-full bg-cocoa px-4 py-2 text-sm text-white shadow-lg">
        {message}
      </div>
    ) : null;

  return {
    showToast: (value: string) => setMessage(value),
    ToastHost,
  };
}
